"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Popup = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ReactDOM = require("react-dom");
const react_focus_lock_1 = require("react-focus-lock");
const CSSTransition_1 = require("react-transition-group/CSSTransition");
const TransitionGroup_1 = require("react-transition-group/TransitionGroup");
const MosaicIcon_1 = require("Views/Shared/Components/MosaicIcon");
const types_1 = require("./types");
var KeyboardKeyEnum;
(function (KeyboardKeyEnum) {
    KeyboardKeyEnum["Esc"] = "Escape";
    KeyboardKeyEnum["EscShort"] = "Esc";
})(KeyboardKeyEnum || (KeyboardKeyEnum = {}));
const removePopup = (container, containerClassName = '') => {
    var _a;
    container.className = (_a = container.className) === null || _a === void 0 ? void 0 : _a.replace(containerClassName, '');
};
const setContainerStyle = (container, dialog) => {
    container.style.overflow = 'hidden';
    container.style.paddingRight = '';
};
const removeContainerStyle = (container) => {
    container.style.overflow = '';
    container.style.paddingRight = '';
};
const Popup = ({ isOpened, isAnimation = true, isKeyboard = true, backdrop = true, isCloseButton = true, disableFocusLock = false, onClose = () => {
    return;
}, container = document.body, containerClassName = 'popup-open', children, className, backdropClassName, popupAriaLabel, popupType = types_1.PopupType.DEFAULT, focusLockWhiteList, onShow, onBackdropClick, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, onEnter, onEntering, onEntered, onExit, onExited, onExiting,
// eslint-disable-next-line sonarjs/cognitive-complexity
 }) => {
    const popupRef = (0, react_1.useRef)(null);
    const [isExited, setIsExited] = (0, react_1.useState)(!isOpened);
    if (!isAnimation && !isOpened && !isExited) {
        setIsExited(true);
    }
    else if (isOpened && isExited) {
        setIsExited(false);
    }
    // focus dialog for accessibility
    (0, react_1.useEffect)(() => {
        var _a;
        if (isOpened) {
            (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [isOpened]);
    const handleTransitionExited = (0, react_1.useCallback)(() => {
        setIsExited(true);
        if (onExited) {
            onExited();
        }
    }, [onExited, setIsExited]);
    const handleDocumentKeyDown = (0, react_1.useCallback)((e) => {
        if (isKeyboard && (e.key === KeyboardKeyEnum.Esc || e.key === KeyboardKeyEnum.EscShort)) {
            //handleEscapeKeyDown(e);
            if (!e.defaultPrevented) {
                onClose();
            }
        }
    }, [isKeyboard, onClose]);
    const handleExited = (0, react_1.useCallback)(() => {
        removePopup(container, containerClassName);
        removeContainerStyle(container);
        window.removeEventListener('keydown', handleDocumentKeyDown);
    }, [container, containerClassName, handleDocumentKeyDown]);
    // handle exited
    (0, react_1.useEffect)(() => {
        if (!isExited)
            return;
        handleExited();
    }, [isExited, handleExited]);
    (0, react_1.useEffect)(() => {
        if (!isOpened || !container)
            return;
        container.className = `${container.className.trim()} ${containerClassName}`;
        setContainerStyle(container, popupRef.current);
        window.addEventListener('keydown', handleDocumentKeyDown);
        if (onShow) {
            onShow();
        }
    }, [isOpened, container, containerClassName, onShow, handleDocumentKeyDown, popupRef]);
    (0, react_1.useEffect)(() => {
        const handleResize = () => {
            if (window.innerWidth < 760 && popupType === types_1.PopupWrapper[types_1.PopupType.MEDIA]) {
                onClose(); // Hide modal 
            }
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    // unMount
    (0, react_1.useEffect)(() => () => {
        handleExited();
    }, [handleExited]);
    const handleBackdropClick = (0, react_1.useCallback)((e) => {
        if (e.target !== e.currentTarget) {
            return;
        }
        if (onBackdropClick) {
            onBackdropClick(e);
        }
        if (backdrop === true) {
            onClose();
        }
    }, [backdrop, onClose, onBackdropClick]);
    const handleContainerClick = (0, react_1.useCallback)((e) => {
        if (backdrop === 'static') {
            return;
        }
        if (e.target !== e.currentTarget) {
            return;
        }
        onClose();
    }, [backdrop, onClose]);
    const transitionProps = (0, react_1.useMemo)(() => isAnimation
        ? {
            appear: true,
            unmountOnExit: true,
            onExit,
            onExiting,
            onEntering,
            onEntered,
            onExited: handleTransitionExited,
            onEnter,
        }
        : {}, [isAnimation, handleTransitionExited, onEnter, onExit, onExiting, onEntering, onEntered]);
    return ((0, jsx_runtime_1.jsx)(TransitionGroup_1.default, { children: isOpened ? ((0, jsx_runtime_1.jsx)(CSSTransition_1.default, Object.assign({ classNames: '', timeout: 10 }, transitionProps, { children: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: ReactDOM.createPortal((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [backdrop ? ((0, jsx_runtime_1.jsx)("div", { id: "popup-backdrop", onClick: handleBackdropClick, className: `popup-backdrop
                      ${types_1.PopupBackDrop[popupType]}
                      ${backdropClassName}
                      ${!isAnimation && 'show'}
                      `, "data-testid": "backdrop" })) : null, (0, jsx_runtime_1.jsx)("div", { ref: popupRef, className: `${className} ${types_1.PopupWrapper[popupType]}`, role: "dialog", "aria-modal": "true", "aria-label": popupAriaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, tabIndex: -1, onClick: backdrop ? handleContainerClick : undefined, "data-testid": "dialog", children: (0, jsx_runtime_1.jsxs)(react_focus_lock_1.default, { className: `${types_1.PopupContainer[popupType]} popup-container`, returnFocus: true, whiteList: focusLockWhiteList, disabled: disableFocusLock, children: [(0, jsx_runtime_1.jsx)("div", { id: "popup-content", className: `${types_1.PopupContent[popupType]} popup-content`, role: "document", children: children }), isCloseButton && ((0, jsx_runtime_1.jsx)("button", { className: `close-button ${types_1.closeBtnPosition[popupType]}`, onClick: onClose, children: (0, jsx_runtime_1.jsx)(MosaicIcon_1.MosaicIcon, { name: "close-outline", className: "bg-fill-secondary hover:bg-fill-secondary-hovered active:bg-fill-secondary-pressed p-2xs absolute top-[10px] right-[10px] rounded-full hover:cursor-pointer group-[.desktop-modal-gallery]:top-[135px] group-[.desktop-modal-gallery]:right-[290px] group-[.desktop-modal-gallery]:z-[99]" }) }))] }) })] }), container) }) }))) : null }));
};
exports.Popup = Popup;
exports.default = exports.Popup;
